import { useEffect, useState } from "react";
import { Fingerprint } from "lucide-react";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { Button } from "./ui";
import { ApiError } from "@/lib/api";
import { cancelled, passkeysSupported, signInOptions, signInWithPasskey } from "@/lib/passkeys";
import type { MeDTO } from "@shared/types";

/** Challenges last 3 minutes on the server; swap in a fresh one before an idle screen's runs out. */
const REFRESH_MS = 150_000;

/** Sign in with whichever passkey the device offers. Renders nothing where passkeys can't work. */
export function PasskeySignIn({ onDone, className, primary = false }: { onDone: (me: MeDTO) => void; className?: string; primary?: boolean }) {
  // Fetched ahead of time so a tap can go straight to Face ID (Safari needs the tap).
  const [options, setOptions] = useState<PublicKeyCredentialRequestOptionsJSON | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const supported = passkeysSupported();

  useEffect(() => {
    if (!supported) return;
    let live = true;
    const load = () =>
      signInOptions()
        .then((o) => live && setOptions(o))
        .catch(() => live && setOptions(null));
    void load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [supported]);

  async function signIn() {
    if (!options) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await signInWithPasskey(options));
    } catch (err) {
      if (!cancelled(err)) setError(err instanceof ApiError ? err.message : "That passkey didn't work. Try another way to sign in.");
      // Each try uses up its challenge; get a fresh one for the next tap.
      setOptions(await signInOptions().catch(() => null));
    } finally {
      setBusy(false);
    }
  }

  if (!options) return null;
  return (
    <div className={className}>
      <Button variant={primary ? undefined : "secondary"} size="lg" block onClick={signIn} loading={busy}>
        <Fingerprint className="size-5" /> Sign in with a passkey
      </Button>
      {error && <p className="mt-2 text-center font-bold text-danger">{error}</p>}
      {primary && (
        <div className="mt-4 flex items-center gap-3 text-sm font-bold text-muted">
          <span className="h-px flex-1 bg-line" />
          or use your password
          <span className="h-px flex-1 bg-line" />
        </div>
      )}
    </div>
  );
}
